import React from "react";
import { Marker } from "react-map-gl/mapbox";
import { AvatarFill } from "./AvatarMarker";
import { logError } from "../../utils/logger";

// ============================================================================
// CONFIGURATION
// ============================================================================

const PRIMARY = "var(--clr-primary-default)";

const AVATAR_SIZE = 34;
const AVATAR_OVERLAP = -28;
const PILL_PADDING = 2;
const PILL_BORDER = 1.5;

const STEM_WIDTH = 2;
const STEM_HEIGHT = 12;
const TIP_SIZE = 4;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ClusterMarker({ cluster, onClick, zIndex }) {
  const { id, longitude, latitude, count, avatars = [], allViewed = false } = cluster;

  const isDark = document.documentElement.classList.contains("dark");

  // Seen/unseen visual treatment
  const MUTED = isDark ? "#4b5563" : "#6b7280";
  const color = allViewed ? MUTED : PRIMARY;
  const containerOpacity = allViewed ? 0.65 : 1;

  const displayAvatars = avatars.slice(0, 3);
  const pillBg = isDark ? "#1e2030" : "#e5e7eb";

  const displayCount = count > 99 ? "99+" : String(count);
  const badgeSize = displayCount.length > 2 ? 24 : 20;

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  const handleClick = (e) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(id, longitude, latitude);
    } catch (error) {
      logError("Error handling cluster click:", error);
    }
  };

  const handleMouseEnter = (e) => {
    try {
      if (e.currentTarget) {
        e.currentTarget.style.transform = "translateY(-2px)";
      }
    } catch (error) {
      logError("Error handling mouse enter:", error);
    }
  };

  const handleMouseLeave = (e) => {
    try {
      if (e.currentTarget) {
        e.currentTarget.style.transform = "translateY(0px)";
      }
    } catch (error) {
      logError("Error handling mouse leave:", error);
    }
  };

  const handleMouseDown = (e) => {
    try {
      if (e.currentTarget) {
        e.currentTarget.style.transform = "translateY(0px)";
      }
    } catch (error) {
      logError("Error handling mouse down:", error);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      anchor="bottom"
      style={zIndex != null ? { zIndex } : undefined}
    >
      <button
        type="button"
        onClick={handleClick}
        aria-label={`Cluster of ${count} notes`}
        title={`${count} notes`}
        className="group relative focus:outline-none"
        style={{
          transform: "translateY(0px)",
          transition: "transform 120ms",
          animation: "markerPopIn 200ms ease-out",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
      >
        <div className="flex flex-col items-center" style={{ opacity: containerOpacity }}>
          {/* Pill container */}
          <div
            style={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              background: pillBg,
              border: `${PILL_BORDER}px solid ${color}`,
              borderRadius: 999,
              padding: PILL_PADDING,
            }}
          >
            {/* Avatar circles */}
            {displayAvatars.map((av, i) => (
              <div
                key={i}
                className="rounded-full overflow-hidden flex-shrink-0"
                style={{
                  width: AVATAR_SIZE,
                  height: AVATAR_SIZE,
                  border: `1px solid ${allViewed ? color : "var(--clr-primary-600)"}`,
                  marginLeft: i === 0 ? 0 : AVATAR_OVERLAP,
                  position: "relative",
                  zIndex: displayAvatars.length - i,
                  filter: allViewed ? "grayscale(100%)" : "none",
                }}
              >
                <AvatarFill src={av.avatar} name={av.name} size={AVATAR_SIZE} />
              </div>
            ))}

            {/* Count badge */}
            <div
              className="absolute rounded-full flex items-center justify-center"
              style={{
                width: badgeSize,
                height: badgeSize,
                backgroundColor: color,
                top: -6,
                right: -6,
                zIndex: 10,
                border: `1.5px solid ${isDark ? pillBg : "white"}`,
              }}
            >
              <span
                className="font-bold text-white leading-none"
                style={{ fontSize: displayCount.length > 2 ? 9 : 10 }}
              >
                {displayCount}
              </span>
            </div>
          </div>

          {/* Stem */}
          <div
            aria-hidden="true"
            style={{
              width: STEM_WIDTH,
              height: STEM_HEIGHT,
              background: `linear-gradient(to bottom, ${color}, transparent)`,
            }}
          />

          {/* Tip dot */}
          <div
            aria-hidden="true"
            className="rounded-full"
            style={{
              width: TIP_SIZE,
              height: TIP_SIZE,
              backgroundColor: allViewed ? MUTED : "var(--clr-primary-400)",
              opacity: 0.5,
            }}
          />
        </div>
      </button>
    </Marker>
  );
}
